import React, { useState, useRef, useEffect } from 'react';
import { getConsultations, createConsultation, deleteConsultation, getImages, uploadImage, deleteImage, getISAKList, createISAK, deleteISAK, updateISAKTitle, getRecommendations, createRecommendation, updateRecommendation, deleteRecommendation, getPatientEditData, updatePatientFields } from '../api';
import useRegistrosConHistorial from '../hooks/useRegistrosConHistorial';
import FichaISAK from './FichaISAK';
import FichaKinesio from './FichaKinesio';
import ModalExportarPDF from '../components/ModalExportarPDF';
import ModalConfirmacion from '../components/ModalConfirmacion';

function calcularEdad(fechaNacimiento) {
  if (!fechaNacimiento) return null;
  const hoy = new Date();
  const nacimiento = new Date(fechaNacimiento);
  let edad = hoy.getFullYear() - nacimiento.getFullYear();
  const mes = hoy.getMonth() - nacimiento.getMonth();
  if (mes < 0 || (mes === 0 && hoy.getDate() < nacimiento.getDate())) edad--;
  return edad >= 0 ? edad : null;
}

function FichaPaciente({ paciente, onGuardar, onActualizarPaciente, onPacienteInactivado }) {
  const [modoEdicion, setModoEdicion] = useState(false);
  const [nombre, setNombre] = useState(paciente.nombre);
  const [campos, setCampos] = useState(paciente.camposExtra.map((c) => ({ ...c })));
  const [guardado, setGuardado] = useState(false);
  const [nuevaFecha, setNuevaFecha] = useState('');
  const [nuevaNota, setNuevaNota] = useState('');
  const [sesionGuardada, setSesionGuardada] = useState(false);
  const [sesionAbierta, setSesionAbierta] = useState(null);
  const [sesiones, setSesiones] = useState([]);
  const inputImagenRef = useRef(null);
  const [imagenes, setImagenes] = useState([]);
  const [imagenAmpliada, setImagenAmpliada] = useState(null);
  const [descargandoImagen, setDescargandoImagen] = useState(false);
  const [errorDescargaImagen, setErrorDescargaImagen] = useState('');

  // Confirmaciones
  const [confirmSesion, setConfirmSesion]     = useState(null); // id de sesión a eliminar
  const [confirmImagen, setConfirmImagen]     = useState(null); // id de imagen a eliminar
  const [confirmCampo, setConfirmCampo]       = useState(null); // id de campo a eliminar
  const [confirmInactivo, setConfirmInactivo] = useState(false);
  const [confirmActivar, setConfirmActivar]   = useState(false);

  // Sistema de fichas ISAK (múltiples) — modo edición por-registro via hook compartido
  const isakHook = useRegistrosConHistorial([]);
  const {
    records: isakRecords,
    abierto: isakAbiertoMap,
    setAbierto: setIsakAbiertoMap,
    toggleAbierto: toggleISAKAcordeon,
    editando: isakEditando,
  } = isakHook;
  const [isakTitulosEdit, setIsakTitulosEdit] = useState({}); // { recordId: string } para editar título inline
  const [isakModuloAbierto, setIsakModuloAbierto] = useState(true);
  const [modalExportar, setModalExportar] = useState(false);
  const [kinesioData, setKinesioData] = useState(null);
  // Recomendaciones — múltiples registros con fecha, modo edición por-registro
  // via hook compartido. La creación de una recomendación nueva se mantiene
  // aparte (recomNueva): el backend exige texto no vacío, así que a diferencia
  // de ISAK/GMFCS/Threshold no se puede crear un registro en blanco de entrada
  // (agregarNuevo() del hook fallaría contra la API) — solo se llama a la API
  // una vez que el usuario completó texto y fecha válidos, momento en el que
  // ya entra a la lista con contenido real. Por eso este módulo no necesita
  // (ni usa) agregarNuevo()/nuncaGuardado: nunca existe un registro fantasma.
  const recomHook = useRegistrosConHistorial([]);
  const [recomAbierta, setRecomAbierta] = useState(true);
  // Accordions de Historial de Sesiones e Imágenes (arrancan expandidos)
  const [sesionesAbierta, setSesionesAbierta] = useState(true);
  const [imagenesAbierta, setImagenesAbierta] = useState(true);
  // Mapa de Google colapsable (cerrado por defecto para no cortar el ritmo visual)
  const [mapaAbierto, setMapaAbierto] = useState(false);
  // Formulario de nueva sesión: oculto tras "+ Nueva sesión" (el historial va primero)
  const [mostrarFormSesion, setMostrarFormSesion] = useState(false);
  const [recomNueva, setRecomNueva] = useState(null); // { text, date } pendiente de guardar
  const [recomNuevaGuardando, setRecomNuevaGuardando] = useState(false);
  const refISAKMap = useRef({}); // { recordId: domNode }
  const btnExportarPrincipalRef = useRef(null);
  const btnExportarRecomRef = useRef(null);
  const btnActivarRef = useRef(null);
  const [confirmActivarRect, setConfirmActivarRect] = useState(null);

  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const esNutricionista  = user.profession === 'nutritionist';
  const esKinesiologo    = user.profession === 'physiotherapist';

  const isActivo = paciente.is_active !== false;

  // Chip de área kinesiológica en el header: solo se muestra una vez que el
  // perfil cargó y tiene un área definida, para no parpadear con un chip
  // vacío mientras FichaKinesio hace su fetch inicial.
  const AREA_LABELS = { motor: 'Área Motora', respiratory: 'Área Respiratoria', both: 'Área Motora y Respiratoria' };
  const areaLabel = esKinesiologo && kinesioData?.area ? AREA_LABELS[kinesioData.area] : null;

  useEffect(() => {
    const cargar = async () => {
      try {
        const data = await getConsultations(paciente.id);
        setSesiones(data);
      } catch (err) {
        console.error("Error cargando consultas:", err);
      }
    };
    cargar();
  }, [paciente.id]);

  useEffect(() => {
    const cargar = async () => {
      try {
        const data = await getImages(paciente.id);
        setImagenes(data);
      } catch (err) {
        console.error("Error cargando imágenes:", err);
      }
    };
    cargar();
  }, [paciente.id]);

  useEffect(() => {
    if (!esNutricionista) return;
    const cargarISAK = async () => {
      try {
        const records = await getISAKList(paciente.id);
        isakHook.setRecords(records);
      } catch (err) {
        isakHook.setRecords([]);
      }
    };
    cargarISAK();
  }, [paciente.id, esNutricionista]); // eslint-disable-line react-hooks/exhaustive-deps

  const agregarFichaISAK = async () => {
    try {
      await isakHook.agregarNuevo(() => createISAK(paciente.id), {});
      setIsakModuloAbierto(true);
    } catch (err) { console.error('Error creando ficha ISAK:', err); }
  };

  const isakEliminarConfirmado = async () => {
    const recordId = isakHook.confirmarEliminarId;
    if (!recordId) return;
    try {
      await isakHook.eliminar(recordId, () => deleteISAK(paciente.id, recordId));
    } catch (err) { console.error('Error eliminando ficha ISAK:', err); }
  };

  const guardarTituloISAK = async (recordId, titulo) => {
    try {
      await updateISAKTitle(paciente.id, recordId, titulo);
      isakHook.setRecords(prev => prev.map(r => r.id === recordId ? { ...r, title: titulo } : r));
    } catch (err) { console.error('Error actualizando título ISAK:', err); }
  };

  const formatearFechaISAK = (isoString) => {
    if (!isoString) return '';
    const d = new Date(isoString);
    return d.toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  // Fecha "plana" (YYYY-MM-DD, sin componente horario) como data.fecha_evaluacion
  // de una ficha ISAK. Se formatea por partes en vez de `new Date(...)` para no
  // arrastrar el corrimiento de un día que introduce interpretar YYYY-MM-DD como
  // medianoche UTC y mostrarlo en un huso horario negativo (ej. Argentina).
  const formatearFechaEvaluacionISAK = (fechaPlana) => {
    if (!fechaPlana) return '';
    const [y, m, d] = fechaPlana.split('-');
    return `${d}/${m}/${y}`;
  };

  const entrarModoEdicion = async () => {
    try {
      const data = await getPatientEditData(paciente.id);
      const camposFormateados = data.fields.map(f => ({
        id: f.field_id,
        field_id: f.field_id,
        label: f.label,
        value: f.value,
        eliminable: !f.system,
        choices: f.choices || null,
        type: f.type || 'CharField',
      }));
      setCampos(camposFormateados);
      setModoEdicion(true);
    } catch (err) { console.error("Error cargando campos:", err); }
  };

  const guardar = async (e) => {
    e.preventDefault();
    try {
      await updatePatientFields(paciente.id, campos.map(c => ({ field: c.field_id, value: c.value, label: c.label })));
      const data = await getPatientEditData(paciente.id);
      const camposActualizados = data.fields.map(f => ({
        id: f.field_id, field_id: f.field_id, label: f.label, value: f.value,
        eliminable: !f.system, choices: f.choices || null, type: f.type || 'CharField',
      }));
      const nombreCompleto = `${data.name}`.trim();
      setCampos(camposActualizados);
      setNombre(nombreCompleto);
      if (onActualizarPaciente) onActualizarPaciente({ ...paciente, nombre: nombreCompleto, camposExtra: camposActualizados });
      setModoEdicion(false);
      setGuardado(true);
      setTimeout(() => setGuardado(false), 2000);
    } catch (err) { console.error("Error guardando campos:", err); }
  };

  const agregarCampo = () => {
    setCampos(prev => [...prev, { id: `temp-${Date.now()}`, field_id: null, label: "", value: "", eliminable: true, choices: null, type: 'CharField' }]);
  };

  const eliminarCampoConfirmado = (id) => setCampos(prev => prev.filter((c) => c.id !== id));
  const cambiarLabel = (id, label) => setCampos(prev => prev.map(c => c.id === id ? { ...c, label } : c));
  const cambiarValue = (id, value) => setCampos(prev => prev.map(c => c.id === id ? { ...c, value } : c));

  const autoResize = (el) => { if (!el) return; el.style.height = 'auto'; el.style.height = el.scrollHeight + 'px'; };
  const refAutoResize = (el) => { if (el) setTimeout(() => autoResize(el), 0); };

  const guardarSesion = async () => {
    if (!nuevaFecha || !nuevaNota) return;
    try {
      const nueva = await createConsultation(paciente.id, nuevaFecha, nuevaNota);
      setSesiones(prev => [nueva, ...prev]);
      setNuevaFecha(''); setNuevaNota('');
      setSesionGuardada(true);
      setTimeout(() => setSesionGuardada(false), 2000);
    } catch (err) { console.error("Error guardando sesión:", err); }
  };

  const confirmarEliminarSesion = async () => {
    try {
      await deleteConsultation(confirmSesion);
      setSesiones(prev => prev.filter(s => s.id !== confirmSesion));
    } catch (err) { console.error("Error eliminando sesión:", err); }
    setConfirmSesion(null);
  };

  const confirmarEliminarImagen = async () => {
    try {
      await deleteImage(confirmImagen);
      setImagenes(prev => prev.filter(i => i.id !== confirmImagen));
    } catch (err) { console.error("Error eliminando imagen:", err); }
    setConfirmImagen(null);
  };

  const confirmarInactivar = async () => {
    setConfirmInactivo(false);
    try {
      await updatePatientFields(paciente.id, [{ field: 'is_active', value: 'false', label: 'Activo' }]);
      if (onPacienteInactivado) onPacienteInactivado();
    } catch (err) { console.error("Error inactivando paciente:", err); }
  };

  const confirmarActivar = async () => {
    try {
      await updatePatientFields(paciente.id, [{ field: 'is_active', value: 'true', label: 'Activo' }]);
      if (onActualizarPaciente) onActualizarPaciente({ ...paciente, is_active: true });
    } catch (err) { console.error("Error activando paciente:", err); }
  };

  const toggleSesion = (id) => setSesionAbierta(prev => (prev === id ? null : id));

  const formatearFecha = (f) => {
    if (!f) return '';
    const [y, m, d] = f.split('-');
    return `${d}/${m}/${y}`;
  };

  const manejarImagen = async (e) => {
    const archivos = Array.from(e.target.files);
    for (const archivo of archivos) {
      try {
        const data = await uploadImage(paciente.id, archivo);
        setImagenes(prev => [data, ...prev]);
      } catch (err) { console.error("Error subiendo imagen:", err); }
    }
    e.target.value = '';
  };

  // Controller de la descarga en curso (si hay una) — permite abortarla si el
  // usuario cierra el lightbox o cambia de imagen antes de que termine.
  const descargaAbortRef = useRef(null);

  // Sin esto, el mensaje de error de una descarga fallida quedaría pegado
  // en pantalla al pasar a otra imagen del lightbox. También cancela una
  // descarga en curso en vez de dejarla terminar sobre una imagen que ya
  // no está en pantalla.
  useEffect(() => {
    setErrorDescargaImagen('');
    if (descargaAbortRef.current) {
      descargaAbortRef.current.abort();
      descargaAbortRef.current = null;
      setDescargandoImagen(false);
    }
  }, [imagenAmpliada]);

  const extensionDesdeMime = (mime) => {
    const mapa = { 'image/jpeg': 'jpg', 'image/png': 'png', 'image/gif': 'gif', 'image/webp': 'webp' };
    return mapa[mime] || 'jpg';
  };

  const nombreArchivoDescarga = (img, blob) => {
    if (img.name) return img.name;
    const fecha = img.uploaded_at ? new Date(img.uploaded_at).toISOString().slice(0, 10) : new Date().toISOString().slice(0, 10);
    const nombrePaciente = (paciente.nombre || 'paciente').trim().replace(/\s+/g, '_');
    return `${nombrePaciente}_${fecha}.${extensionDesdeMime(blob.type)}`;
  };

  const descargarImagenLightbox = async () => {
    if (!imagenAmpliada || descargandoImagen) return;
    setErrorDescargaImagen('');
    setDescargandoImagen(true);
    const controller = new AbortController();
    descargaAbortRef.current = controller;
    try {
      const res = await fetch(imagenAmpliada.image_url, { signal: controller.signal });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const objectUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = objectUrl;
      a.download = nombreArchivoDescarga(imagenAmpliada, blob);
      document.body.appendChild(a);
      a.click();
      a.remove();
      // Revocar sincrónicamente puede cortar la descarga a mitad de camino
      // en Safari iOS, que procesa el click sintético con delay.
      setTimeout(() => URL.revokeObjectURL(objectUrl), 100);
    } catch (err) {
      if (err.name !== 'AbortError') {
        console.error('Error descargando imagen:', err);
        setErrorDescargaImagen('No se pudo descargar la imagen. Probá de nuevo.');
      }
    } finally {
      // Si este controller ya no es el vigente, el useEffect de arriba ya
      // se encargó de resetear el estado (o ya hay una descarga más nueva
      // en curso que no hay que pisar).
      if (descargaAbortRef.current === controller) {
        descargaAbortRef.current = null;
        setDescargandoImagen(false);
      }
    }
  };

  // ── Recomendaciones ─────────────────────────────────────────────────────────

  useEffect(() => {
    const cargar = async () => {
      try {
        const data = await getRecommendations(paciente.id);
        recomHook.setRecords(data);
      } catch (err) { console.error('Error cargando recomendaciones:', err); }
    };
    cargar();
  }, [paciente.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const todayISO = () => new Date().toISOString().slice(0, 10);

  const recomAgregarNueva = () => {
    setRecomNueva({ text: '', date: todayISO() });
    setRecomAbierta(true);
  };

  const recomGuardarNueva = async () => {
    if (!recomNueva?.date) return;
    setRecomNuevaGuardando(true);
    try {
      const saved = await createRecommendation(paciente.id, recomNueva);
      recomHook.setRecords(prev => [saved, ...prev]);
      setRecomNueva(null);
    } catch (err) { console.error('Error creando recomendación:', err); }
    finally { setRecomNuevaGuardando(false); }
  };

  const recomIniciarEdicion = (rec) => {
    recomHook.iniciarEdicion(rec.id, { text: rec.text, date: rec.date });
  };

  const recomGuardar = async (id) => {
    try {
      await recomHook.guardar(id, (edicion) => updateRecommendation(paciente.id, id, {
        text: edicion.text,
        date: edicion.date,
      }));
    } catch (err) { console.error('Error actualizando recomendación:', err); }
  };

  const recomEliminarConfirmado = async () => {
    const id = recomHook.confirmarEliminarId;
    if (!id) return;
    try {
      await recomHook.eliminar(id, () => deleteRecommendation(paciente.id, id));
    } catch (err) { console.error('Error eliminando recomendación:', err); }
  };

  const exportarSoloRecomendaciones = () => {
    const rect = btnExportarRecomRef.current?.getBoundingClientRect() || null;
    setModalExportar({ modo: 'recomendaciones', anchorRect: rect });
  };

  const abrirExportarPrincipal = () => {
    const rect = btnExportarPrincipalRef.current?.getBoundingClientRect() || null;
    setModalExportar({ modo: 'normal', anchorRect: rect });
  };

  // is_active → banner; maps_link → embed en address; recommendations → sección propia debajo
  const camposGrilla = campos.filter(c => !['is_active', 'maps_link', 'recommendations'].includes(c.field_id));

  const edadCalculada = calcularEdad(
    campos.find(c => c.field_id === 'birth_date')?.value || paciente.birth_date
  );

  // ── Agrupación de campos en bloques lógicos ──────────────────────────────────
  // Personales / Contacto / Clínicos. Los campos personalizados (field_id numérico)
  // y cualquier campo de sistema no listado caen en "Clínicos".
  const CAMPOS_PERSONALES = ['first_name', 'last_name', 'birth_date', 'sex'];
  const CAMPOS_CONTACTO   = ['address', 'city'];
  const grupoDeCampo = (fid) => {
    if (CAMPOS_PERSONALES.includes(fid)) return 'personales';
    if (CAMPOS_CONTACTO.includes(fid)) return 'contacto';
    return 'clinicos';
  };
  const camposPorGrupo = (grupo) =>
    camposGrilla.filter(c => grupoDeCampo(c.field_id) === grupo && (modoEdicion || c.value));

  // ── Línea-resumen de identidad (solo lectura, debajo del nombre) ─────────────
  const valorCampo = (fid) => campos.find(c => c.field_id === fid)?.value || '';
  const sexoResumen = (() => {
    const campoSexo = campos.find(c => c.field_id === 'sex');
    const v = campoSexo?.value;
    if (!v) return '';
    return campoSexo.choices?.find(o => o.value === v)?.label
      || { M: 'Masculino', F: 'Femenino', O: 'Otro' }[v] || v;
  })();
  const resumenIdentidad = [
    edadCalculada !== null ? `${edadCalculada} años` : '',
    sexoResumen,
    valorCampo('consultation_reason'),
    valorCampo('city') || valorCampo('address'),
  ].filter(Boolean);

  const renderCampoEdicion = (campo) => {
    // Date picker para fecha de nacimiento
    if (campo.field_id === 'birth_date') {
      return (
        <input
          type="date"
          className="ficha-input date-input"
          value={campo.value || ''}
          onChange={(e) => cambiarValue(campo.id, e.target.value)}
          onClick={(e) => e.target.showPicker?.()}
        />
      );
    }
    // Selector para campos con choices
    if (campo.choices && campo.choices.length > 0) {
      return (
        <select className="ficha-input" value={campo.value} onChange={(e) => cambiarValue(campo.id, e.target.value)}>
          <option value="">Seleccionar...</option>
          {campo.choices.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
        </select>
      );
    }
    // URL field: input normal
    if (campo.type === 'URLField') {
      return (
        <input
          type="url"
          className="ficha-input"
          value={campo.value || ''}
          onChange={(e) => cambiarValue(campo.id, e.target.value)}
          placeholder="https://maps.google.com/..."
        />
      );
    }
    // TextField (multi-línea)
    return (
      <textarea
        className="ficha-input"
        rows="1"
        value={campo.value || ''}
        onChange={(e) => { cambiarValue(campo.id, e.target.value); autoResize(e.target); }}
        placeholder="Contenido..."
        ref={refAutoResize}
      />
    );
  };

  const renderCampoVisualizacion = (campo) => {
    if (campo.field_id === 'birth_date' && campo.value) {
      return <div className="ficha-campo-valor">{formatearFecha(campo.value)}</div>;
    }
    if (campo.field_id === 'address' && campo.value) {
      return (
        <div>
          <div className="ficha-campo-valor">{campo.value}</div>
          <button
            type="button"
            className="maps-toggle-btn"
            onClick={() => setMapaAbierto(p => !p)}
            aria-expanded={mapaAbierto}
          >
            📍 {mapaAbierto ? 'Ocultar mapa' : 'Ver mapa'} <span className="sesion-chevron">{mapaAbierto ? '▲' : '▼'}</span>
          </button>
          {mapaAbierto && (
            <div className="maps-embed-wrapper">
              <iframe
                title="Mapa"
                src={`https://maps.google.com/maps?q=${encodeURIComponent(campo.value)}&output=embed&hl=es`}
                className="maps-embed-iframe"
                loading="lazy"
                allowFullScreen
              />
            </div>
          )}
        </div>
      );
    }
    if (campo.choices && campo.choices.length > 0) {
      const label = campo.choices.find(c => c.value === campo.value)?.label || campo.value;
      return <div className="ficha-campo-valor">{label}</div>;
    }
    return <div className="ficha-campo-valor">{campo.value || ''}</div>;
  };

  // Render de una tarjeta de campo (devuelve array: [tarjeta] o [tarjeta, edad])
  const renderTarjetaCampo = (campo) => {
    const claseExtra =
      (campo.field_id === 'consultation_reason' || campo.field_id === 'pathological_background') ? ' campo-card-full' :
      (campo.field_id === 'address' && !modoEdicion) ? ' campo-card-full' :
      '';
    const tarjeta = (
      <div
        key={campo.id}
        className={`campo-card${modoEdicion ? ' campo-card-edit' : ''}${claseExtra}`}
      >
        <div className="campo-card-header">
          {modoEdicion && campo.eliminable ? (
            <input
              type="text"
              className="campo-card-label-editable"
              value={campo.label}
              onChange={(e) => cambiarLabel(campo.id, e.target.value)}
              placeholder="Título del campo"
            />
          ) : (
            <span className="campo-card-label">{campo.label || 'Sin título'}</span>
          )}
          {modoEdicion && campo.eliminable && (
            <button type="button" className="btn-eliminar-campo" onClick={() => setConfirmCampo(campo.id)}>X</button>
          )}
        </div>
        {modoEdicion ? renderCampoEdicion(campo) : renderCampoVisualizacion(campo)}
      </div>
    );

    if (campo.field_id === 'birth_date' && edadCalculada !== null) {
      const edadCard = (
        <div key="edad-auto" className={`campo-card${modoEdicion ? ' campo-card-edit' : ''}`}>
          <span className="campo-card-label">Edad</span>
          <div className="ficha-campo-valor">{edadCalculada} años</div>
        </div>
      );
      return [tarjeta, edadCard];
    }
    return [tarjeta];
  };

  // Render de un grupo de campos como bloque con subtítulo. Devuelve null si vacío.
  const renderGrupoCampos = (grupo, titulo) => {
    const lista = camposPorGrupo(grupo);
    if (lista.length === 0) return null;
    return (
      <div className="datos-grupo">
        <div className="datos-grupo-titulo">{titulo}</div>
        <div className="datos-grupo-grid">
          {lista.flatMap(renderTarjetaCampo)}
        </div>
      </div>
    );
  };

  return (
    <div className="ficha">

      {/* ══════════ ZONA A · DATOS DEL PACIENTE ══════════ */}

      {/* 1. Banner prominente — solo cuando el paciente está INACTIVO */}
      {!isActivo && (
        <div className="ficha-estado-banner ficha-estado-inactivo">
          <span>⚠ Paciente INACTIVO</span>
          <button
            ref={btnActivarRef}
            type="button"
            className="btn-activar"
            onClick={() => {
              setConfirmActivarRect(btnActivarRef.current?.getBoundingClientRect() ?? null);
              setConfirmActivar(true);
            }}
          >
            Reactivar paciente
          </button>
        </div>
      )}

      {/* 2. Header de identidad: nombre protagonista + resumen + acciones */}
      <div className="ficha-identidad">
        <div className="ficha-identidad-info">
          <div className="ficha-identidad-titulo-row">
            <h2 className="ficha-nombre">{modoEdicion ? `Editando — ${nombre}` : nombre}</h2>
            {isActivo && <span className="ficha-estado-chip">ACTIVO</span>}
            {areaLabel && <span className="ficha-area-chip">{areaLabel}</span>}
          </div>
          {resumenIdentidad.length > 0 && (
            <div className="ficha-identidad-resumen">
              {resumenIdentidad.map((t, i) => (
                <React.Fragment key={i}>
                  {i > 0 && <span className="ficha-resumen-sep">·</span>}
                  <span>{t}</span>
                </React.Fragment>
              ))}
            </div>
          )}
        </div>
        <div className="ficha-identidad-acciones">
          <button ref={btnExportarPrincipalRef} type="button" className="btn-exportar" onClick={abrirExportarPrincipal}>
            Exportar PDF
          </button>
          {isActivo && (
            <button type="button" className="btn-inactivar" onClick={() => setConfirmInactivo(true)}>
              Marcar inactivo
            </button>
          )}
          <button
            className={`btn-editar ${modoEdicion ? 'btn-editar-activo' : ''}`}
            onClick={modoEdicion ? () => setModoEdicion(false) : entrarModoEdicion}
          >
            {modoEdicion ? 'Cancelar' : 'Editar'}
          </button>
        </div>
      </div>

      {/* 3. Datos del paciente — agrupados en bloques lógicos */}
      <form onSubmit={guardar} className="ficha-datos-form">
        {renderGrupoCampos('personales', 'Datos personales')}
        {renderGrupoCampos('contacto', 'Contacto')}
        {renderGrupoCampos('clinicos', 'Datos clínicos')}

        {modoEdicion && (
          <div className="ficha-acciones">
            <button type="submit" className="btn-guardar" disabled={guardado}>
              {guardado ? 'Guardado ✓' : 'Guardar Cambios'}
            </button>
            <button type="button" className="btn-guardar btn-agregar-campo" onClick={agregarCampo}>
              + Agregar campo
            </button>
          </div>
        )}
      </form>

      {/* Módulo ISAK — evaluaciones antropométricas (nutricionistas) */}
      {esNutricionista && (
        <div className="modulo-clinico-wrapper">
          <div className="sesiones-header">
            <button type="button" className="acordeon-toggle-btn" onClick={() => setIsakModuloAbierto(p => !p)}>
              <h3 className="sesiones-titulo" style={{ margin: 0 }}>Fichas ISAK (Antropometría)</h3>
              <span className="sesion-chevron">{isakModuloAbierto ? '▲' : '▼'}</span>
            </button>
            <button type="button" className="btn-guardar-sesion sesiones-btn-nueva" onClick={agregarFichaISAK}>
              + Nueva ficha ISAK
            </button>
          </div>

          {isakModuloAbierto && (
            isakRecords.length === 0 ? (
              <p className="sesiones-vacio">No hay fichas ISAK registradas aún.</p>
            ) : (
              <div className="isak-lista-wrapper">
                {isakRecords.map((record) => {
                  const abierto = !!isakAbiertoMap[record.id];
                  const editableISAK = !!isakEditando[record.id];
                  const tituloEditando = isakTitulosEdit[record.id] !== undefined
                    ? isakTitulosEdit[record.id]
                    : record.title;

                  return (
                    <div key={record.id} className="plantilla-acordeon">
                      <div className={`plantilla-acordeon-btn ${abierto ? 'plantilla-acordeon-btn-activo' : ''}`}>
                        {/* Título editable en modo edición */}
                        <div className="isak-acordeon-izq" onClick={() => toggleISAKAcordeon(record.id)}>
                          {modoEdicion ? (
                            <input
                              className="isak-titulo-input"
                              value={tituloEditando}
                              onChange={(e) => setIsakTitulosEdit(prev => ({ ...prev, [record.id]: e.target.value }))}
                              onBlur={() => {
                                guardarTituloISAK(record.id, tituloEditando);
                                setIsakTitulosEdit(prev => { const n = { ...prev }; delete n[record.id]; return n; });
                              }}
                              onClick={(e) => e.stopPropagation()}
                              placeholder="Título de la evaluación"
                            />
                          ) : (
                            <span className="isak-acordeon-titulo">
                              {record.title || 'Evaluación Antropométrica ISAK'}
                            </span>
                          )}
                          <span className="isak-acordeon-fecha">
                            {record.data?.fecha_evaluacion
                              ? `Evaluación del ${formatearFechaEvaluacionISAK(record.data.fecha_evaluacion)}`
                              : 'Sin fecha de evaluación'}
                          </span>
                        </div>
                        <div className="isak-acordeon-der" onClick={() => toggleISAKAcordeon(record.id)}>
                          <span className="sesion-chevron">{abierto ? '▲' : '▼'}</span>
                        </div>
                      </div>

                      {abierto && (
                        <div
                          className="plantilla-acordeon-contenido"
                          ref={(el) => { refISAKMap.current[record.id] = el; }}
                        >
                          <FichaISAK
                            key={`isak-${record.id}-${editableISAK}`}
                            pacienteId={paciente.id}
                            recordId={record.id}
                            editable={editableISAK}
                            onEditar={() => isakHook.iniciarEdicion(record.id, {})}
                            onCancelar={() => isakHook.cancelarEdicion(record.id, () => deleteISAK(paciente.id, record.id))}
                            onGuardado={(actualizado) => {
                              isakHook.setRecords(prev => prev.map(r => r.id === record.id ? actualizado : r));
                              // FichaISAK guarda directo contra la API (no pasa por hook.guardar()),
                              // así que hay que avisarle al hook a mano que este registro ya tiene
                              // contenido real guardado — si no, un Cancelar posterior lo borraría.
                              isakHook.marcarGuardado(record.id);
                              // Mismo delay que el "Guardado ✓" transitorio de FichaISAK, para
                              // salir de edición y colapsar el acordeón recién se apaga el mensaje.
                              // Usa cerrarEdicion (no cancelarEdicion): un setTimeout diferido puede
                              // disparar sobre un closure de un render viejo, y cancelarEdicion ahí
                              // leería un nuncaGuardado desactualizado y borraría el registro recién
                              // guardado. cerrarEdicion no depende de ningún estado capturado.
                              setTimeout(() => isakHook.cerrarEdicion(record.id), 2500);
                            }}
                            onEliminar={() => isakHook.pedirEliminar(record.id)}
                            datosIniciales={record.data || {}}
                            paciente={paciente}
                          />
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )
          )}
        </div>
      )}

      {/* ── Módulo Kinesiología ── */}
      {esKinesiologo && (
        <div className="modulo-clinico-wrapper">
          <FichaKinesio
            pacienteId={paciente.id}
            key={`kinesio-${paciente.id}`}
            onDataChange={setKinesioData}
          />
        </div>
      )}

      {/* ══════════ ZONA B · ACTIVIDAD CLÍNICA ══════════ */}
      <div className="zona-clinica">
      <div className="zona-titulo">Actividad clínica</div>

      {/* 5. Recomendaciones / Sugerencias — múltiples registros con fecha */}
      <div className="recom-seccion">
        <div className="recom-header">
          <button type="button" className="acordeon-toggle-btn" onClick={() => setRecomAbierta(p => !p)}>
            <h3 className="sesiones-titulo" style={{ margin: 0 }}>Recomendaciones / Sugerencias</h3>
            <span className="sesion-chevron">{recomAbierta ? '▲' : '▼'}</span>
          </button>
          <div className="recom-header-acciones">
            {recomHook.records.length > 0 && (
              <button ref={btnExportarRecomRef} type="button" className="btn-exportar recom-btn-exportar" onClick={exportarSoloRecomendaciones}>
                Exportar
              </button>
            )}
            <button type="button" className="btn-guardar-sesion sesiones-btn-nueva"
              onClick={() => (recomNueva ? setRecomNueva(null) : recomAgregarNueva())}>
              {recomNueva ? 'Cerrar' : '+ Nueva recomendación'}
            </button>
          </div>
        </div>

        {recomAbierta && (
          <div className="recom-cuerpo">
            {/* Nueva recomendación pendiente de guardar */}
            {recomNueva && (
              <div className="recom-card recom-card-nueva">
                <div className="recom-card-fecha-row">
                  <input type="date" className="ficha-input date-input recom-date-input"
                    value={recomNueva.date}
                    onChange={e => setRecomNueva(p => ({ ...p, date: e.target.value }))}
                    onClick={e => e.target.showPicker?.()}
                  />
                  <button type="button" className="recom-btn-hoy"
                    onClick={() => setRecomNueva(p => ({ ...p, date: todayISO() }))}>
                    Hoy
                  </button>
                </div>
                <textarea
                  className="ficha-input recom-textarea"
                  rows="3"
                  autoFocus
                  placeholder="Escribí la recomendación o sugerencia..."
                  value={recomNueva.text}
                  onChange={e => setRecomNueva(p => ({ ...p, text: e.target.value }))}
                />
                <div className="recom-card-acciones">
                  <button type="button" className="modal-btn-cancelar"
                    onClick={() => setRecomNueva(null)} disabled={recomNuevaGuardando}>
                    Cancelar
                  </button>
                  <button type="button" className="btn-guardar-sesion"
                    onClick={recomGuardarNueva}
                    disabled={recomNuevaGuardando || !recomNueva.date || !recomNueva.text.trim()}>
                    {recomNuevaGuardando ? 'Guardando...' : 'Guardar'}
                  </button>
                </div>
              </div>
            )}

            {/* Lista de recomendaciones existentes — colapsadas por defecto, mismo
                patrón de acordeón que ISAK/GMFCS/Threshold/Historial de Sesiones */}
            {recomHook.records.length === 0 && !recomNueva && (
              <p className="sesiones-vacio">No hay recomendaciones registradas aún.</p>
            )}
            {recomHook.records.map(rec => {
              const abierto = !!recomHook.abierto[rec.id];
              const edicion = recomHook.editando[rec.id];
              const fecha = edicion ? edicion.date : rec.date;
              const texto = edicion ? edicion.text : rec.text;

              return (
                <div key={rec.id} className="sesion-acordeon">
                  <button
                    className={`sesion-fecha-btn ${abierto ? 'sesion-fecha-btn-activo' : ''}`}
                    onClick={() => recomHook.toggleAbierto(rec.id)}
                  >
                    <span>Recomendación del {formatearFecha(rec.date)}</span>
                    <span className="sesion-chevron">{abierto ? '▲' : '▼'}</span>
                  </button>

                  {abierto && (
                    <div className="sesion-contenido">
                      {!edicion ? (
                        <>
                          <p className="sesion-nota">{rec.text}</p>
                          <button type="button" className="btn-guardar-sesion" onClick={() => recomIniciarEdicion(rec)}>
                            Editar
                          </button>
                          <button type="button" className="btn-eliminar-sesion" style={{ marginLeft: 8 }}
                            onClick={() => recomHook.pedirEliminar(rec.id)}>
                            Eliminar
                          </button>
                        </>
                      ) : (
                        <>
                          <div className="recom-card-fecha-row">
                            <input type="date" className="ficha-input date-input recom-date-input"
                              value={fecha}
                              onChange={e => recomHook.actualizarEdicion(rec.id, { date: e.target.value })}
                              onClick={e => e.target.showPicker?.()}
                            />
                            <button type="button" className="recom-btn-hoy"
                              onClick={() => recomHook.actualizarEdicion(rec.id, { date: todayISO() })}>
                              Hoy
                            </button>
                          </div>
                          <textarea
                            className="ficha-input recom-textarea"
                            rows="3"
                            value={texto}
                            onChange={e => recomHook.actualizarEdicion(rec.id, { text: e.target.value })}
                            placeholder="Texto de la recomendación..."
                          />
                          <div className="recom-card-acciones">
                            <button type="button" className="modal-btn-cancelar"
                              onClick={() => recomHook.cancelarEdicion(rec.id, () => deleteRecommendation(paciente.id, rec.id))}
                              disabled={recomHook.guardando[rec.id]}>
                              Cancelar
                            </button>
                            <button type="button" className="btn-eliminar-sesion"
                              onClick={() => recomHook.pedirEliminar(rec.id)}>
                              Eliminar
                            </button>
                            <button type="button" className="btn-guardar-sesion"
                              onClick={() => recomGuardar(rec.id)}
                              disabled={recomHook.guardando[rec.id] || !fecha}>
                              {recomHook.guardado[rec.id] ? 'Guardado ✓' : recomHook.guardando[rec.id] ? 'Guardando...' : 'Guardar'}
                            </button>
                          </div>
                        </>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* 6. Historial de sesiones — el historial primero; el formulario tras un botón */}
      <div className="sesiones-seccion">
        <div className="sesiones-header">
          <button type="button" className="acordeon-toggle-btn" onClick={() => setSesionesAbierta(p => !p)}>
            <h3 className="sesiones-titulo" style={{ margin: 0 }}>Historial de Sesiones</h3>
            <span className="sesion-chevron">{sesionesAbierta ? '▲' : '▼'}</span>
          </button>
          <button type="button" className="btn-guardar-sesion sesiones-btn-nueva"
            onClick={() => setMostrarFormSesion(p => !p)}>
            {mostrarFormSesion ? 'Cerrar' : '+ Nueva sesión'}
          </button>
        </div>

        {sesionesAbierta && (
          <>
            {mostrarFormSesion && (
              <div className="nueva-sesion">
                <label className="ficha-label">
                  Fecha de la sesión
                  <div className="recom-card-fecha-row">
                    <input type="date" className="ficha-input date-input" value={nuevaFecha} onChange={(e) => setNuevaFecha(e.target.value)} onClick={(e) => e.target.showPicker?.()} />
                    <button type="button" className="recom-btn-hoy" onClick={() => setNuevaFecha(todayISO())}>
                      Hoy
                    </button>
                  </div>
                </label>
                <label className="ficha-label">
                  Anotaciones
                  <textarea className="ficha-input" rows="4" placeholder="Escribí las anotaciones de la sesión..." value={nuevaNota} onChange={(e) => setNuevaNota(e.target.value)} />
                </label>
                <button type="button" className="btn-guardar-sesion" onClick={guardarSesion} disabled={sesionGuardada || !nuevaFecha || !nuevaNota}>
                  {sesionGuardada ? 'Sesión guardada ✓' : 'Guardar Sesión'}
                </button>
              </div>
            )}

            {sesiones.length === 0 ? (
              <p className="sesiones-vacio">No hay sesiones registradas aún.</p>
            ) : (
              <div className="sesiones-lista">
                {sesiones.map((sesion) => (
                  <div key={sesion.id} className="sesion-acordeon">
                    <button className={`sesion-fecha-btn ${sesionAbierta === sesion.id ? 'sesion-fecha-btn-activo' : ''}`}
                      onClick={() => toggleSesion(sesion.id)}>
                      <span>Sesión del {formatearFecha(sesion.date)}</span>
                      <span className="sesion-chevron">{sesionAbierta === sesion.id ? '▲' : '▼'}</span>
                    </button>
                    {sesionAbierta === sesion.id && (
                      <div className="sesion-contenido">
                        <p className="sesion-nota">{sesion.notes}</p>
                        <button className="btn-eliminar-sesion" onClick={() => setConfirmSesion(sesion.id)}>
                          Eliminar sesión
                        </button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>

      {/* 7. Imágenes — adjuntos, al final de la actividad clínica */}
      <div className="imagenes-seccion">
        <div className="imagenes-header">
          <button type="button" className="acordeon-toggle-btn" onClick={() => setImagenesAbierta(p => !p)}>
            <h3 className="sesiones-titulo" style={{ margin: 0 }}>Imágenes</h3>
            <span className="sesion-chevron">{imagenesAbierta ? '▲' : '▼'}</span>
          </button>
          <button type="button" className="btn-agregar-imagen" onClick={() => inputImagenRef.current.click()}>
            + Agregar imagen
          </button>
          <input ref={inputImagenRef} type="file" accept="image/*" multiple style={{ display: 'none' }} onChange={manejarImagen} />
        </div>
        {imagenesAbierta && (
          imagenes.length === 0 ? (
            <p className="sesiones-vacio">No hay imágenes cargadas aún.</p>
          ) : (
            <div className="imagenes-grid">
              {[...imagenes]
                .sort((a, b) => new Date(a.uploaded_at || 0) - new Date(b.uploaded_at || 0))
                .map((img) => (
                <div key={img.id} className="imagen-item">
                  <img src={img.image_url} alt={img.name} className="imagen-preview"
                    onClick={() => setImagenAmpliada(img)} style={{ cursor: 'pointer' }} />
                  <div className="imagen-footer">
                    <div className="imagen-info">
                      <span className="imagen-nombre">{img.name}</span>
                      {img.uploaded_at && (
                        <span className="imagen-fecha">{formatearFechaISAK(img.uploaded_at)}</span>
                      )}
                    </div>
                    <button className="btn-eliminar-imagen" onClick={() => setConfirmImagen(img.id)}>✕</button>
                  </div>
                </div>
              ))}
            </div>
          )
        )}
      </div>

      {/* Lightbox */}
      {imagenAmpliada && (
        <div className="lightbox-overlay" onClick={() => setImagenAmpliada(null)}>
          <img src={imagenAmpliada.image_url} alt={imagenAmpliada.name} className="lightbox-img"
            onClick={(e) => e.stopPropagation()} />
          <div className="lightbox-barra" onClick={(e) => e.stopPropagation()}>
            <span className="lightbox-nombre">{imagenAmpliada.name}</span>
            <div className="lightbox-acciones">
              <button type="button" className="lightbox-btn lightbox-btn-descargar"
                onClick={descargarImagenLightbox} disabled={descargandoImagen}
                aria-label={descargandoImagen ? 'Descargando imagen' : 'Descargar imagen'}>
                {descargandoImagen ? (
                  <span className="lightbox-spinner" aria-hidden="true" />
                ) : (
                  <span aria-hidden="true">⬇</span>
                )}
                <span className="lightbox-btn-texto">{descargandoImagen ? 'Descargando…' : 'Descargar'}</span>
              </button>
              <button type="button" className="lightbox-btn lightbox-btn-cerrar"
                onClick={() => setImagenAmpliada(null)} aria-label="Cerrar">
                <span aria-hidden="true">✕</span>
                <span className="lightbox-btn-texto">Cerrar</span>
              </button>
            </div>
          </div>
          {errorDescargaImagen && (
            <div className="lightbox-error" onClick={(e) => e.stopPropagation()}>{errorDescargaImagen}</div>
          )}
        </div>
      )}

      </div>{/* /zona-clinica */}

      {/* Modal exportar PDF */}
      {modalExportar && (() => {
        const user = JSON.parse(localStorage.getItem('user') || '{}');
        const camposPDF = [
          ...(campos.find(c => c.field_id === 'first_name') ? [{ label: 'Nombre', value: campos.find(c => c.field_id === 'first_name')?.value }] : []),
          ...(campos.find(c => c.field_id === 'last_name') ? [{ label: 'Apellido', value: campos.find(c => c.field_id === 'last_name')?.value }] : []),
          ...campos.filter(c => !['first_name','last_name','is_active','maps_link'].includes(c.field_id) && c.value),
        ];
        const anchorRect = typeof modalExportar === 'object' ? modalExportar.anchorRect : null;
        return (
          <ModalExportarPDF
            nombre={nombre}
            campos={camposPDF}
            sesiones={sesiones}
            isakRecords={isakRecords}
            refISAKMap={refISAKMap}
            isakAbiertoMap={isakAbiertoMap}
            setIsakAbiertoMap={setIsakAbiertoMap}
            isakModuloAbierto={isakModuloAbierto}
            setIsakModuloAbierto={setIsakModuloAbierto}
            firmaUrl={user.signature_url || null}
            firmaTexto={user.signature_text || null}
            recomRecords={recomHook.records}
            kinesioData={esKinesiologo ? kinesioData : null}
            anchorRect={anchorRect}
            modoInicial={typeof modalExportar === 'object' ? modalExportar.modo : 'normal'}
            onCerrar={() => setModalExportar(false)}
          />
        );
      })()}

      {/* Modales de confirmación */}
      {confirmInactivo && (
        <ModalConfirmacion
          mensaje={`¿Marcar a "${nombre}" como INACTIVO?`}
          textoAlerta="Esto eliminará automáticamente todos sus turnos futuros del calendario."
          textoConfirmar="Marcar inactivo"
          onConfirmar={confirmarInactivar}
          onCancelar={() => setConfirmInactivo(false)}
        />
      )}
      {confirmActivar && (
        <ModalConfirmacion
          mensaje={`¿Reactivar a "${nombre}"?`}
          textoAlerta="El paciente volverá a aparecer en la lista principal y podrás agendar nuevas sesiones."
          textoConfirmar="Reactivar paciente"
          onConfirmar={async () => { setConfirmActivar(false); await confirmarActivar(); }}
          onCancelar={() => setConfirmActivar(false)}
          anchorRect={confirmActivarRect}
        />
      )}
      {confirmSesion && (
        <ModalConfirmacion
          mensaje="¿Eliminar esta sesión? Esta acción no se puede deshacer."
          textoConfirmar="Eliminar sesión"
          onConfirmar={confirmarEliminarSesion}
          onCancelar={() => setConfirmSesion(null)}
        />
      )}
      {confirmImagen && (
        <ModalConfirmacion
          mensaje="¿Eliminar esta imagen? Esta acción no se puede deshacer."
          textoConfirmar="Eliminar imagen"
          onConfirmar={confirmarEliminarImagen}
          onCancelar={() => setConfirmImagen(null)}
        />
      )}
      {confirmCampo && (
        <ModalConfirmacion
          mensaje="¿Eliminar este campo personalizado? Se perderán todos sus valores."
          textoConfirmar="Eliminar campo"
          onConfirmar={() => { eliminarCampoConfirmado(confirmCampo); setConfirmCampo(null); }}
          onCancelar={() => setConfirmCampo(null)}
        />
      )}
      {recomHook.confirmarEliminarId && (
        <ModalConfirmacion
          mensaje="¿Eliminar esta recomendación? Esta acción no se puede deshacer."
          textoConfirmar="Eliminar recomendación"
          onConfirmar={recomEliminarConfirmado}
          onCancelar={recomHook.cancelarPedidoEliminar}
        />
      )}
      {isakHook.confirmarEliminarId && (
        <ModalConfirmacion
          mensaje="¿Eliminar esta ficha ISAK? Esta acción no se puede deshacer."
          textoConfirmar="Eliminar ficha"
          onConfirmar={isakEliminarConfirmado}
          onCancelar={isakHook.cancelarPedidoEliminar}
        />
      )}

    </div>
  );
}

export default FichaPaciente;
